import { useInterval } from '@hooks/useInterval';
import { getAvailableLayers } from '@utils/map/map.utils';
import { useEffect, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';

import { FeatureTypeEnum, LayerNameEnum, SourceNameEnum } from '@/types/map.types';

interface UseContingencyBlinkingProps {
  sourceName?: SourceNameEnum;
  layerNames?: LayerNameEnum[];
  featureTypes?: FeatureTypeEnum | FeatureTypeEnum[];
  blinkInterval?: number;
}

export function useContingencyBlinking(props: Readonly<UseContingencyBlinkingProps> = {}) {
  const {
    sourceName = SourceNameEnum.TARGETS,
    layerNames = [LayerNameEnum.TARGETS, LayerNameEnum.TARGETS_CONTINGENCY],
    featureTypes = FeatureTypeEnum.TARGET_ICON,
    blinkInterval = 1000,
  } = props;
  const { current: map } = useMap();
  const [toggle, setToggle] = useState<boolean>(false);

  useInterval(() => {
    setToggle(!toggle);
  }, blinkInterval);

  useEffect(() => {
    if (!map || !map.isStyleLoaded()) return;

    const availableLayers = getAvailableLayers(map.getMap(), layerNames);
    const features = map.queryRenderedFeatures({ layers: availableLayers });

    if (features.length === 0 || !map.getSource(sourceName)) return;

    const featureTypesArray = Array.isArray(featureTypes) ? featureTypes : [featureTypes];

    for (const feature of features) {
      if (
        feature.id !== undefined &&
        feature.properties?.id &&
        featureTypesArray.includes(feature.properties.featureType) &&
        JSON.parse(feature.properties.value)?.collaborativeData?.inContingency === true
      ) {
        map.setFeatureState(
          { source: sourceName, id: feature.id },
          {
            blinkOn: toggle,
          },
        );
      }
    }
  }, [blinkInterval, featureTypes, layerNames, map, sourceName, toggle]);
}
